import { API_URLS } from "@/config/urls";
import type {
  FilesResponse,
  Folder,
  FoldersResponse,
} from "@/features/study-group-files/types/file-types";
import { handleAxiosError, isAxiosError } from "@/lib/apiErrors";
import { api } from "@/lib/axios";

export interface CreateFolderInput {
  containerId: number;
  title: string;
  description: string;
  visibility: number;
  isAdminOnly?: boolean;
  parentFolderId?: number | null;
}

export interface UpdateFolderInput {
  folderId: number;
  title: string;
  description: string;
  isAdminOnly: boolean;
}

export const filesApi = {
  getFileByGuid: async (guid: string): Promise<Blob> => {
    try {
      const response = await api.get(
        `${API_URLS.FILE}/download-by-guid/${guid}`,
        {
          responseType: "blob", // バイナリデータを直接Blobとして受け取る
          headers: {
            Accept: "image/*,*/*",
          },
        },
      );

      // レスポンスヘッダーからContent-Typeを取得
      const contentType =
        response.headers["content-type"] ||
        response.headers["Content-Type"] ||
        "image/png";

      // Blobの型が正しく設定されていない場合は修正
      if (
        !response.data.type ||
        response.data.type === "application/octet-stream"
      ) {
        return new Blob([response.data], { type: contentType });
      }

      return response.data;
    } catch (error: unknown) {
      console.error(`Error fetching file with GUID ${guid}:`, error);

      if (isAxiosError(error)) {
        handleAxiosError(error, "An error occurred while fetching the file.");
      }

      throw error instanceof Error
        ? error
        : new Error("Unexpected error occurred");
    }
  },
  getPublicFileByGuid: async (guid: string): Promise<Blob> => {
    try {
      const response = await api.get(
        `${API_URLS.FILE}/public-download-by-guid/${guid}`,
        {
          responseType: "blob", // バイナリデータを直接Blobとして受け取る
          headers: {
            Accept: "image/*,*/*",
          },
        },
      );

      // レスポンスヘッダーからContent-Typeを取得
      const contentType =
        response.headers["content-type"] ||
        response.headers["Content-Type"] ||
        "image/png";

      // Blobの型が正しく設定されていない場合は修正
      if (
        !response.data.type ||
        response.data.type === "application/octet-stream"
      ) {
        return new Blob([response.data], { type: contentType });
      }

      return response.data;
    } catch (error: unknown) {
      console.error(`Error fetching file with GUID ${guid}:`, error);

      if (isAxiosError(error)) {
        handleAxiosError(error, "An error occurred while fetching the file.");
      }

      throw error instanceof Error
        ? error
        : new Error("Unexpected error occurred");
    }
  },

  getFoldersByContainer: async (
    containerId: number,
  ): Promise<FoldersResponse> => {
    try {
      const response = await api.get(
        `${API_URLS.C_FILES}/folders/container/${containerId}`,
      );
      return response.data;
    } catch (error: unknown) {
      console.error(
        `Error fetching folders for container ${containerId}:`,
        error,
      );

      if (isAxiosError(error)) {
        handleAxiosError(error, "An error occurred while fetching folders.");
      }

      throw error instanceof Error
        ? error
        : new Error("Unexpected error occurred");
    }
  },

  getFilesByContainer: async (containerId: number): Promise<FilesResponse> => {
    try {
      const response = await api.get(
        `${API_URLS.C_FILES}/files/container/${containerId}`,
      );
      return response.data;
    } catch (error: unknown) {
      console.error(
        `Error fetching files for container ${containerId}:`,
        error,
      );

      if (isAxiosError(error)) {
        handleAxiosError(error, "An error occurred while fetching files.");
      }

      throw error instanceof Error
        ? error
        : new Error("Unexpected error occurred");
    }
  },

  createFolder: async ({
    containerId,
    title,
    description,
    visibility,
    isAdminOnly,
    parentFolderId,
  }: CreateFolderInput): Promise<Folder> => {
    try {
      const payload = {
        target_id: parentFolderId,
        Folder: {
          title,
          description,
          visibility,
          ...(isAdminOnly ? { is_admin_only: true } : {}),
        },
      };

      const response = await api.post(
        `${API_URLS.C_FILES}/folders/container/${containerId}`,
        payload,
      );
      return response.data;
    } catch (error: unknown) {
      console.error(
        `Error creating folder in container ${containerId}:`,
        error,
      );

      if (isAxiosError(error)) {
        handleAxiosError(error, "An error occurred while creating the folder.");
      }

      throw error instanceof Error
        ? error
        : new Error("Unexpected error occurred");
    }
  },

  deleteFolder: async (folderId: number): Promise<void> => {
    try {
      await api.delete(`${API_URLS.C_FILES}/folder/${folderId}`);
    } catch (error: unknown) {
      console.error(`Error deleting folder ${folderId}:`, error);

      if (isAxiosError(error)) {
        handleAxiosError(error, "An error occurred while deleting the folder.");
      }

      throw error instanceof Error
        ? error
        : new Error("Unexpected error occurred");
    }
  },

  editFolder: async ({
    folderId,
    title,
    description,
    isAdminOnly,
  }: UpdateFolderInput): Promise<void> => {
    try {
      await api.put(`${API_URLS.C_FILES}/folder/${folderId}`, {
        Folder: {
          title,
          description,
          is_admin_only: isAdminOnly,
        },
      });
    } catch (error: unknown) {
      console.error(`Error editing folder ${folderId}:`, error);

      if (isAxiosError(error)) {
        handleAxiosError(error, "An error occurred while updating the folder.");
      }

      throw error instanceof Error
        ? error
        : new Error("Unexpected error occurred");
    }
  },

  uploadFiles: async (
    containerId: number,
    files: globalThis.File[],
    parentFolderId?: number | null,
  ): Promise<{ code: number; message: string }> => {
    try {
      const formData = new FormData();

      // Add each file to the form data
      files.forEach((file) => {
        formData.append("files[]", file);
      });

      // Add target_id (parent folder only)
      if (parentFolderId) {
        formData.append("folder_id", parentFolderId.toString());
      }

      const response = await api.post(
        `${API_URLS.C_FILES}/files/container/${containerId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return response.data;
    } catch (error: unknown) {
      console.error(
        `Error uploading files to container ${containerId}:`,
        error,
      );

      if (isAxiosError(error)) {
        handleAxiosError(error, "An error occurred while uploading files.");
      }

      throw error instanceof Error
        ? error
        : new Error("Unexpected error occurred");
    }
  },
  deleteFile: async (fileId: number): Promise<void> => {
    try {
      await api.delete(`${API_URLS.C_FILES}/file/${fileId}`);
    } catch (error: unknown) {
      console.error(`Error deleting file ${fileId}:`, error);

      if (isAxiosError(error)) {
        handleAxiosError(error, "An error occurred while deleting the file.");
      }

      throw error instanceof Error
        ? error
        : new Error("Unexpected error occurred");
    }
  },
};
